// =============================================
// Author Page Generator (C1 z planu SRO)
//
// Re-runnable entry point dla strony "O autorze" — generuje HTML + ProfilePage schema.org.
// Operuje na thematic_writing_profiles (kolumny autora juz istnieja w schemacie).
//
// Pomija stages Content Engine: NO SERP, NO KG, NO Fan-Out (irrelevant dla author bio).
// Tylko: load profile + load map context + LLM call author_page_generation prompt.
//
// Gated VITE_AUTHOR_PAGE_ENABLED (default false) + feature flag per profile (author_page_enabled).
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider, getStageConfig } from "@/services/llm";
import { compilePromptSafe } from "./promptCompiler";
import { buildProfilePageSchema } from "@/services/schema/schemaGenerator";
import type { SchemaPayload } from "@/services/schema/types";
import { logPublic, logDebug, logError, logVerbose, logLlm, startLogRun, endLogRun } from "./contentLogger";

export interface AuthorPageDebug {
  prompt_key: string;
  prompt_version: number | null;
  model: string;
  model_id: string | null;
  temperature: number | null;
  compiled_prompt: string;        // FULL compiled prompt (logVerbose only persistuje, ale return klient widzi w UI)
  compiled_prompt_chars: number;
  template_vars: Record<string, string>;
  raw_response_text: string;      // FULL raw LLM response BEFORE post-processing
  raw_response_chars: number;
  duration_ms: number;
  tokens_input: number | null;
  tokens_output: number | null;
  cost_usd: number;
}

export interface AuthorPageResult {
  success: boolean;
  html?: string;
  schema?: SchemaPayload;
  costUsd: number;
  profileId: string;
  mapId: string | null;
  error?: string;
  debug?: AuthorPageDebug;
}

const AUTHOR_PAGE_FALLBACK = `<h2>O autorze</h2><p>Bio nie zostało jeszcze wygenerowane.</p>`;

/**
 * Generate author page HTML + ProfilePage schema dla profilu.
 * @param profileId thematic_writing_profiles.id
 * @param mapId optional — gdy podany, lacza context (macro, top seeds, top entities) z mapy
 * @param onProgress optional callback
 */
export async function generateAuthorPage(
  profileId: string,
  mapId: string | null = null,
  onProgress?: (msg: string) => void,
): Promise<AuthorPageResult> {
  const runId = `author-page-${profileId.slice(0, 8)}-${Date.now()}`;
  startLogRun(runId, { type: "author_page", profileId, mapId });
  logPublic("AuthorPage", `START — profile=${profileId.slice(0, 8)}, map=${mapId?.slice(0, 8) || "(none)"}`);
  onProgress?.("Loading profile...");
  const startTime = Date.now();

  // 1. Load profile (full)
  const { data: profile, error: profileErr } = await (supabase as any)
    .from("thematic_writing_profiles")
    .select("*")
    .eq("id", profileId)
    .single();

  if (profileErr || !profile) {
    const msg = `Profile ${profileId} not found: ${profileErr?.message || "no data"}`;
    logError("AuthorPage", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, costUsd: 0, profileId, mapId, error: msg };
  }

  // 2. Load map context (optional)
  let macroStatement = "";
  let topSeedNames = "";
  let topEntityNames = "";

  if (mapId) {
    onProgress?.("Loading map context...");
    const { data: mapData } = await (supabase as any)
      .from("thematic_maps")
      .select("name, macro_statement")
      .eq("id", mapId)
      .single();

    macroStatement = mapData?.macro_statement || "";

    const { data: seeds } = await (supabase as any)
      .from("thematic_seeds")
      .select("name, priority")
      .eq("map_id", mapId)
      .order("priority", { ascending: false })
      .limit(10);
    topSeedNames = (seeds || []).map((s: any) => s.name).join(", ");

    const { data: entities } = await (supabase as any)
      .from("thematic_entities")
      .select("canonical_name")
      .eq("map_id", mapId)
      .limit(10);
    topEntityNames = (entities || []).map((e: any) => e.canonical_name).join(", ");
  }

  // 3. Load prompt from DB (author_page_generation v1, is_active=true od STOP-A.1)
  const promptDb = await loadPrompt("author_page_generation" as any);
  if (!promptDb) {
    const msg = "Prompt 'author_page_generation' not found in DB (is_active=true required)";
    logError("AuthorPage", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, costUsd: 0, profileId, mapId, error: msg };
  }
  logDebug("AuthorPage", `Prompt loaded: author_page_generation v${promptDb.version} model=${promptDb.modelId || promptDb.model}`, {
    prompt_key: "author_page_generation",
    version: promptDb.version,
    model: promptDb.model,
    model_id: promptDb.modelId,
    temperature: promptDb.temperature,
  });

  // 4. Build template vars (compilePromptSafe-safe)
  const voiceChars = profile.voice_characteristics
    ? typeof profile.voice_characteristics === "string"
      ? profile.voice_characteristics
      : JSON.stringify(profile.voice_characteristics)
    : "";

  const templateVars: Record<string, string> = {
    public_author_label: profile.public_author_label || "",
    author_identity: profile.author_identity || "",
    author_bio_html: profile.author_bio_html || "",
    author_url: profile.author_url || "",
    macro_statement: macroStatement,
    top_seed_names: topSeedNames,
    top_entity_names: topEntityNames,
    voice_characteristics: voiceChars,
  };

  const compiledPrompt = compilePromptSafe(
    promptDb.template,
    templateVars,
    "author_page_generation",
    AUTHOR_PAGE_FALLBACK,
  );

  logDebug("AuthorPage", `Prompt compiled: ${compiledPrompt.length} chars`, {
    template_vars: templateVars,
    prompt_preview: compiledPrompt.slice(0, 400),
  });
  // Full compiled prompt — only persisted gdy VITE_LOG_LEVEL=verbose
  logVerbose("AuthorPage", "Compiled prompt (FULL)", { prompt: compiledPrompt });

  // 5. LLM call (delegowane do provider)
  onProgress?.("Generating content...");
  const stageConfig = getStageConfig("draft");
  const provider = getLLMProvider(promptDb.provider || stageConfig.provider);
  const model = promptDb.modelId || stageConfig.model;

  let html = AUTHOR_PAGE_FALLBACK;
  let costUsd = 0;
  let rawResponseText = "";
  let tokensInput: number | null = null;
  let tokensOutput: number | null = null;
  let llmDurationMs = 0;
  const llmStart = Date.now();
  try {
    const response = await provider.call(compiledPrompt, {
      model,
      step: "author_page_generation",
      temperature: promptDb.temperature ?? undefined,
    });
    html = response.text || AUTHOR_PAGE_FALLBACK;
    rawResponseText = response.text || "";
    costUsd = response.costUsd ?? 0;
    tokensInput = (response as { tokensInput?: number }).tokensInput ?? null;
    tokensOutput = (response as { tokensOutput?: number }).tokensOutput ?? null;
    llmDurationMs = Date.now() - llmStart;

    // Structured LLM call log (PUBLIC duration + DEBUG details)
    logLlm("author_page_generation", {
      model,
      provider: promptDb.provider || stageConfig.provider,
      temperature: promptDb.temperature,
    }, {
      tokensInput: tokensInput ?? undefined,
      tokensOutput: tokensOutput ?? undefined,
      costUsd,
      durationMs: llmDurationMs,
      responseLength: rawResponseText.length,
    });
    // Full raw response — only persisted gdy VITE_LOG_LEVEL=verbose
    logVerbose("AuthorPage", "Raw LLM response (FULL)", {
      response_text: rawResponseText,
      response_chars: rawResponseText.length,
    });
  } catch (err) {
    const msg = `LLM call failed: ${(err as Error).message}`;
    logError("AuthorPage", msg);
    endLogRun({ success: false, error: msg });
    return { success: false, costUsd: 0, profileId, mapId, error: msg };
  }

  // 6. Build ProfilePage schema (gdy profile ma author_url)
  let schema: SchemaPayload | undefined;
  if (profile.author_url) {
    schema = buildProfilePageSchema({
      profileUrl: profile.author_url,
      publicLabel: profile.public_author_label || "Autor",
      authorIdentity: profile.author_identity,
      authorSlug: profile.author_slug || profileId,
      authorUrl: profile.author_url,
      image: profile.author_photo_url,
      jobTitle: profile.author_job_title,
      bioHtml: profile.author_bio_html,
      knowsAbout: Array.isArray(profile.author_knows_about) ? profile.author_knows_about : [],
      sameAs: Array.isArray(profile.author_same_as) ? profile.author_same_as : [],
      description: profile.author_identity ?? null,
      dateCreated: profile.created_at ?? null,
      dateModified: new Date().toISOString(),
    });
  }

  logPublic(
    "AuthorPage",
    `END — ${html.length} chars HTML, cost=$${costUsd.toFixed(4)}, schema=${schema ? "yes" : "no"}`,
  );

  const debug: AuthorPageDebug = {
    prompt_key: "author_page_generation",
    prompt_version: promptDb.version,
    model: promptDb.model,
    model_id: promptDb.modelId,
    temperature: promptDb.temperature,
    compiled_prompt: compiledPrompt,
    compiled_prompt_chars: compiledPrompt.length,
    template_vars: templateVars,
    raw_response_text: rawResponseText,
    raw_response_chars: rawResponseText.length,
    duration_ms: Date.now() - startTime,
    tokens_input: tokensInput,
    tokens_output: tokensOutput,
    cost_usd: costUsd,
  };

  endLogRun({
    success: true,
    html_chars: html.length,
    cost_usd: costUsd,
    duration_s: Math.round(debug.duration_ms / 1000),
    schema: schema ? "yes" : "no",
  });

  return {
    success: true,
    html,
    schema,
    costUsd,
    profileId,
    mapId,
    debug,
  };
}

/**
 * Re-runnable wrapper — auto-persist do thematic_writing_profiles.author_page_generated_at.
 * NIE pisze do WP — UI/publish flow obsluguje publikacje osobno.
 */
export async function generateAndPersistAuthorPage(
  profileId: string,
  mapId: string | null = null,
  onProgress?: (msg: string) => void,
): Promise<AuthorPageResult> {
  const result = await generateAuthorPage(profileId, mapId, onProgress);

  if (result.success) {
    await (supabase as any)
      .from("thematic_writing_profiles")
      .update({ author_page_generated_at: new Date().toISOString() })
      .eq("id", profileId);
  }

  return result;
}

/**
 * Helper: VITE_AUTHOR_PAGE_ENABLED check (env-time, default false).
 */
export function isAuthorPageEnabled(): boolean {
  const fromVite =
    typeof import.meta !== "undefined" &&
    (import.meta as { env?: { VITE_AUTHOR_PAGE_ENABLED?: string } }).env?.VITE_AUTHOR_PAGE_ENABLED;
  const fromProcess =
    typeof process !== "undefined" ? process.env.VITE_AUTHOR_PAGE_ENABLED : undefined;
  const value = fromVite || fromProcess || "false";
  return value === "true";
}
